/**
 * ChatMarkdown — 对话气泡内的 Markdown 渲染器
 *
 * 专为聊天场景优化：紧凑行距、小号字体、代码块适配气泡宽度。
 * 支持 GFM（表格、删除线、任务列表等）。
 */

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { memo, useState, useEffect, useRef } from 'react'
import type { Components } from 'react-markdown'
import { cn } from '@/lib/utils'

/* ── 链接处理用常量 ─────────────────────────────── */
const RE_WWW_PREFIX = /^www\./
const RE_TRAILING_SLASH = /\/$/

/* ── 自定义渲染组件 ─────────────────────────────── */

const chatComponents: Components = {
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
  ),

  h1: ({ children }) => (
    <h4 className="text-sm font-bold mt-3 mb-1.5 first:mt-0">{children}</h4>
  ),
  h2: ({ children }) => (
    <h4 className="text-sm font-bold mt-3 mb-1.5 first:mt-0">{children}</h4>
  ),
  h3: ({ children }) => (
    <h5 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h5>
  ),

  ul: ({ children }) => (
    <ul className="pl-3.5 mb-2 last:mb-0 list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="pl-3.5 mb-2 last:mb-0 list-decimal space-y-1">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),

  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),

  em: ({ children }) => (
    <em className="italic">{children}</em>
  ),

  a: ({ href, children }) => {
    if (!href) return <span>{children}</span>

    // 提取域名
    let domain = ''
    try {
      domain = new URL(href).hostname.replace(RE_WWW_PREFIX, '')
    } catch {
      domain = href.slice(0, 30)
    }

    // favicon via Google S2
    const faviconUrl = `https://www.google.com/s2/favicons?sz=32&domain=${domain}`

    // 提取纯文本内容
    const text = typeof children === 'string'
      ? children
      : Array.isArray(children)
        ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
        : ''

    // 如果链接文字就是 URL 本身，只显示域名
    const isRawUrl = text === href || text === href.replace(RE_TRAILING_SLASH, '')
    const displayText = isRawUrl ? domain : text || domain

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-secondary/60 px-2.5 py-1 text-[0.9em] text-foreground/90 no-underline shadow-sm align-baseline transition-all duration-200 hover:bg-secondary/80 hover:border-border/80 hover:shadow-md hover:-translate-y-px active:scale-[0.97] active:shadow-sm"
      >
        <img
          src={faviconUrl}
          alt=""
          className="h-3.5 w-3.5 rounded-sm shrink-0"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
        <span className="truncate max-w-[200px]">{displayText}</span>
        {!isRawUrl && (
          <span className="text-muted-foreground/50 text-[0.8em] shrink-0">{domain}</span>
        )}
      </a>
    )
  },

  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-primary/30 pl-3 my-2 text-muted-foreground italic">
      {children}
    </blockquote>
  ),

  hr: () => <hr className="my-2 border-border/40" />,

  /* ── 代码 ── */
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-')
    if (isBlock) {
      const lang = className?.replace('language-', '') ?? ''
      return (
        <div className="my-1.5 last:mb-0 rounded-lg overflow-hidden border border-border/30">
          {lang && (
            <div className="px-3 py-1 text-[11px] text-muted-foreground bg-secondary/60 border-b border-border/20">
              {lang}
            </div>
          )}
          <pre className="p-3 overflow-x-auto bg-secondary/40 text-xs leading-relaxed">
            <code>{children}</code>
          </pre>
        </div>
      )
    }
    return (
      <code className="px-1.5 py-0.5 rounded bg-secondary/60 text-[0.84em] font-mono">
        {children}
      </code>
    )
  },
  pre: ({ children }) => <>{children}</>,

  /* ── 表格 ── */
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 last:mb-0 rounded-sm border border-border/50 shadow-sm">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-secondary/60">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-2.5 py-1.5 text-left font-semibold text-foreground/80 border-b border-border/40">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2.5 py-1.5 border-b border-border/20">{children}</td>
  ),

  /* ── 图片 ── */
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt ?? ''}
      className="max-w-full rounded-lg my-1.5 last:mb-0"
      loading="lazy"
    />
  ),
}

/* ── 主组件 ─────────────────────────────────────── */

interface ChatMarkdownProps {
  content: string
  className?: string
}

export const ChatMarkdown = memo(function ChatMarkdown({
  content,
  className,
}: ChatMarkdownProps) {
  return (
    <div className={cn('text-sm [&>*:first-child]:mt-0', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatComponents}>
        {content}
      </ReactMarkdown>
    </div>
  )
})

/* ── 打字动画 + Markdown 渲染 ──────────────────── */

interface TypingMarkdownProps {
  content: string
  speed?: number
  className?: string
  onComplete?: () => void
}

/** 光标占位符 — 拼在文本末尾，由 markdown 渲染器作为普通文本输出 */
const CURSOR = '\u258E' // ▎ left quarter block，视觉接近光标

/**
 * 逐字展开文本，同时实时渲染 Markdown。
 * 光标跟随在最后一个字符后面，打字完成后自动消失。
 */
export const TypingMarkdown = memo(function TypingMarkdown({
  content,
  speed = 12,
  className,
  onComplete,
}: TypingMarkdownProps) {
  const [displayed, setDisplayed] = useState('')
  const [done, setDone] = useState(false)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    setDisplayed('')
    setDone(false)
    let i = 0
    const interval = setInterval(() => {
      if (i < content.length) {
        i++
        setDisplayed(content.slice(0, i))
      } else {
        clearInterval(interval)
        setDone(true)
        onCompleteRef.current?.()
      }
    }, speed)
    return () => clearInterval(interval)
  }, [content, speed])

  // 把光标拼在文本末尾，让它自然跟随文本流
  const textWithCursor = done ? displayed : displayed + CURSOR

  return (
    <div className={cn('text-sm [&>*:first-child]:mt-0', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatComponents}>
        {textWithCursor}
      </ReactMarkdown>
    </div>
  )
})
